import { useMutation } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CatalogImportRequest } from '@/api/handlers/resources/catalogImports/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportResult } from '@/api/models';

/**
 * Previews a catalog import (`POST catalog-imports/preview`): what importing
 * the body would do, row by row. Writes nothing and caches nothing; the
 * import flow keeps the latest answer itself and re-runs this on every
 * re-check.
 *
 * Unwraps the envelope (rulebook §10): a refusal of the whole request
 * (`IMPORT_TOO_MANY_LINES`, `IMPORT_REQUEST_INVALID`, `BRANCH_NOT_ALLOWED`)
 * rejects with the `ErrorResponse`, code intact. Row and header problems are
 * not refusals: they are in the result.
 */
export const useCatalogImportPreviewMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        CatalogImportResult,
        ErrorResponse,
        CatalogImportRequest
    >({
        mutationFn: async (request) => {
            const result = await apiClient.catalogImports.preview(request);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
    });
};
